import * as React from 'react'
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query'
import { proposalService } from '@/services/ProposalService'
import { clientKeys, proposalKeys, proposalTemplateKeys } from '@/hooks/queryKeys'
import type { ProposalDeliveryDto } from '@lib/dto/proposal-delivery.dto'
import type {
  ProposalDetailDto,
  ProposalDto,
  ProposalVersionDto,
} from '@lib/dto/proposal.dto'
import type {
  CreateProposalInput,
  CreateProposalVersionInput,
  ProposalVersionDecisionInput,
  UpdateProposalInput,
  UpdateProposalVersionInput,
} from '@lib/input/proposal.input'

/**
 * Proposal write operations, grouped because they share one invalidation rule.
 *
 * Any write to a version invalidates the whole PROPOSAL, never just the version
 * — sending v2 changes v1's derived status to Superseded, so refreshing only
 * the row that came back would leave a stale sibling on screen. That is the
 * cost of deriving status instead of storing it, and it is paid here, once.
 */
function useInvalidateProposal(): (proposalId?: string) => void {
  const queryClient = useQueryClient()
  return React.useCallback(
    (proposalId?: string) => {
      void queryClient.invalidateQueries({ queryKey: proposalKeys.lists() })
      if (proposalId) {
        void queryClient.invalidateQueries({
          queryKey: proposalKeys.detail(proposalId),
        })
      }
      // The client detail lists its proposals.
      void queryClient.invalidateQueries({ queryKey: clientKeys.details() })
    },
    [queryClient],
  )
}

export function useUpdateProposal(): UseMutationResult<
  ProposalDto,
  Error,
  { id: string; input: UpdateProposalInput }
> {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateProposal()
  return useMutation({
    mutationFn: ({ id, input }) => proposalService.update(id, input),
    onSuccess: (_proposal, variables) => {
      invalidate(variables.id)
      // Itemizing changes the draft a client would be shown, which is cached
      // on its own from the proposal record.
      void queryClient.invalidateQueries({ queryKey: proposalKeys.documents() })
      // What it bids picks the wording under its own (see
      // ProposalTemplateService), so a change of work changes that too.
      if (variables.input.work !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: proposalTemplateKeys.proposal(variables.id),
        })
      }
    },
  })
}

export function useCreateProposal(): UseMutationResult<
  ProposalDetailDto,
  Error,
  CreateProposalInput
> {
  const invalidate = useInvalidateProposal()
  return useMutation({
    mutationFn: (input: CreateProposalInput) => proposalService.create(input),
    onSuccess: (proposal) => invalidate(proposal.id),
  })
}

export interface AddVersionVariables {
  proposalId: string
  input: CreateProposalVersionInput
}

export function useAddProposalVersion(): UseMutationResult<
  ProposalVersionDto,
  Error,
  AddVersionVariables
> {
  const invalidate = useInvalidateProposal()
  return useMutation({
    mutationFn: ({ proposalId, input }: AddVersionVariables) =>
      proposalService.addVersion(proposalId, input),
    onSuccess: (_version, variables) => invalidate(variables.proposalId),
  })
}

export interface UpdateVersionVariables {
  versionId: string
  proposalId: string
  input: UpdateProposalVersionInput
}

export function useUpdateProposalVersion(): UseMutationResult<
  ProposalVersionDto,
  Error,
  UpdateVersionVariables
> {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateProposal()
  return useMutation({
    mutationFn: ({ versionId, input }: UpdateVersionVariables) =>
      proposalService.updateVersion(versionId, input),
    onSuccess: async (version, variables) => {
      // The site and photo controls enable as soon as this mutation settles,
      // and the next click rebuilds the whole list from the proposal in the
      // cache. Invalidating only refetches after that, so a second click in
      // the gap was computed from the list this write had just replaced, and
      // the later write won. The version that came back goes into the cache
      // first. A refetch still in flight is cancelled so it cannot land the
      // old list afterwards. The invalidation stays: a sibling version's
      // derived status can change, and the document check has to be redone.
      await queryClient.cancelQueries({
        queryKey: proposalKeys.detail(variables.proposalId),
      })
      queryClient.setQueryData<ProposalDetailDto>(
        proposalKeys.detail(variables.proposalId),
        (current) => {
          if (current === undefined) return current
          return {
            ...current,
            versions: current.versions.map((existing) =>
              existing.id === version.id ? version : existing,
            ),
          }
        },
      )
      invalidate(variables.proposalId)
      void queryClient.invalidateQueries({
        queryKey: proposalKeys.document(variables.versionId),
      })
    },
  })
}

export interface VersionActionVariables {
  versionId: string
  proposalId: string
}

/**
 * Rebuild a draft from its sites' newest visits. Invalidates the document as
 * well as the proposal: the findings it is built from are what change.
 */
export function useSwitchToNewestVisits(): UseMutationResult<
  ProposalVersionDto,
  Error,
  VersionActionVariables
> {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateProposal()
  return useMutation({
    mutationFn: ({ versionId }: VersionActionVariables) =>
      proposalService.switchToNewestVisits(versionId),
    onSuccess: (_version, variables) => {
      invalidate(variables.proposalId)
      void queryClient.invalidateQueries({
        queryKey: proposalKeys.document(variables.versionId),
      })
    },
  })
}

export function useSendProposalVersion(): UseMutationResult<
  ProposalVersionDto,
  Error,
  VersionActionVariables
> {
  const invalidate = useInvalidateProposal()
  return useMutation({
    mutationFn: ({ versionId }: VersionActionVariables) =>
      proposalService.send(versionId),
    onSuccess: (_version, variables) => invalidate(variables.proposalId),
  })
}

/**
 * Email a version to the client.
 *
 * Invalidates the proposal for the same reason sending does — the version
 * becomes frozen — and additionally because the delivery it creates is shown
 * on the version it belongs to.
 */
export function useDeliverProposalVersion(): UseMutationResult<
  ProposalDeliveryDto,
  Error,
  VersionActionVariables
> {
  const invalidate = useInvalidateProposal()
  return useMutation({
    mutationFn: ({ versionId }: VersionActionVariables) =>
      proposalService.deliver(versionId),
    onSuccess: (_delivery, variables) => invalidate(variables.proposalId),
    // Sending stamps the version before the message is handed to the
    // provider. A failure after that stamp still leaves a sent version, and
    // leaving the cache on the old draft is how later edits are offered and
    // then dropped as "already sent".
    onError: (_error, variables) => invalidate(variables.proposalId),
  })
}

export interface DecideVersionVariables extends VersionActionVariables {
  input: ProposalVersionDecisionInput
}

export function useDecideProposalVersion(): UseMutationResult<
  ProposalVersionDto,
  Error,
  DecideVersionVariables
> {
  const invalidate = useInvalidateProposal()
  return useMutation({
    mutationFn: ({ versionId, input }: DecideVersionVariables) =>
      proposalService.decide(versionId, input),
    onSuccess: (_version, variables) => invalidate(variables.proposalId),
  })
}

export interface ArchiveProposalVariables {
  id: string
  archived: boolean
}

export function useArchiveProposal(): UseMutationResult<
  ProposalDto,
  Error,
  ArchiveProposalVariables
> {
  const invalidate = useInvalidateProposal()
  return useMutation({
    mutationFn: ({ id, archived }: ArchiveProposalVariables) =>
      proposalService.setArchived(id, archived),
    onSuccess: (proposal) => invalidate(proposal.id),
  })
}
